import { CheckIcon, PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { ActorRefFrom } from 'xstate';
import type { profileFormMachine } from '../profileForm.machine';
import { ContactCard } from './ContactCard';
import { EmailColgroup } from './contactTableLayout';

const TAGS = CT.profile.contactInfo.email.table;
const CARD_TAGS = CT.profile.contactInfo.email.card;

interface EmailTableProps {
  actor: ActorRefFrom<typeof profileFormMachine>;
  onDelete: (index: number) => void;
}
export const EmailTable = (props: EmailTableProps) => {
  const { actor, onDelete } = props;
  const emailAddresses = useSelector(actor, (state) => state.context.emailAddresses);
  const handleOpen = () => actor.send({ type: 'ON_ADD_EMAIL_ADDRESS' });
  const handleEdit = (index: number) => actor.send({ type: 'ON_EDIT_EMAIL_ADDRESS', index });
  const handleDelete = (index: number) => onDelete(index);
  return (
    <>
      <div className="flex items-center gap-2">
        <div className="flex-auto min-w-0">
          <h2 className="font-semibold text-xl text-content-primary truncate">Email Addresses</h2>
        </div>
        <div className="flex-none">
          <button
            type="button"
            aria-label="Add email address"
            title="Add email address"
            data-cy={TAGS.addEmailBtn}
            onClick={handleOpen}
            className="flex items-center justify-center rounded-lg p-1.5 text-primary-50 border border-primary-t-700 hover:bg-primary-t-900 hover:border-primary-t-600 transition-colors cursor-pointer"
          >
            <PlusCircleIcon className="size-5" aria-hidden="true" />
          </button>
        </div>
      </div>
      {/* [MCV-1332] Desktop: the aligned table. Hidden below md, where a phone
          can't fit the columns without horizontal scroll. */}
      <div className="mt-2 hidden md:block flow-root border-2 border-gray-2 rounded-md p-3">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full table-fixed divide-y divide-gray-2">
              <EmailColgroup />
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-content-secondary sm:pl-0"
                  >
                    Email Address
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-content-secondary"
                  >
                    Primary
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-2">
                {emailAddresses.map((email, index) => (
                  <tr
                    className="text-content-secondary "
                    key={email.emailAddress}
                    data-cy={TAGS.emailRow + index}
                  >
                    <td
                      data-cy={TAGS.emailAddressRow + index}
                      title={email.emailAddress}
                      className="py-4 pr-3 pl-4 text-sm font-medium truncate sm:pl-0"
                    >
                      {email.emailAddress}
                    </td>
                    <td
                      data-cy={TAGS.isPrimaryRpw + index}
                      className="px-3 py-4 text-sm whitespace-nowrap"
                    >
                      {email.primaryFlag && <CheckIcon className="size-6" />}
                    </td>
                    <td className="relative py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-0">
                      <button
                        type="button"
                        className="text-primary-50 hover:text-primary-200 cursor-pointer"
                        onClick={() => handleEdit(index)}
                      >
                        Edit<span className="sr-only">, {email.emailAddress}</span>
                      </button>
                      <button
                        type="button"
                        className="text-error hover:text-error-100 cursor-pointer ml-3"
                        onClick={() => handleDelete(index)}
                      >
                        <TrashIcon className="size-4 inline" />
                        <span className="sr-only">Delete {email.emailAddress}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* [MCV-1332] Mobile: stacked cards instead of a horizontally-scrolling table. */}
      <ul className="md:hidden mt-2 flex flex-col gap-3">
        {emailAddresses.map((email, index) => (
          <ContactCard
            key={email.emailAddress}
            dataCy={CARD_TAGS.row + index}
            title={email.emailAddress}
            isPrimary={email.primaryFlag}
            fields={[]}
            onEdit={() => handleEdit(index)}
            onDelete={() => handleDelete(index)}
            label={email.emailAddress}
          />
        ))}
      </ul>
    </>
  );
};
